import { LocalNotifications } from "@capacitor/local-notifications";
import { isNative } from "@/lib/capacitor";

/**
 * Request notification permission on native platforms.
 * Gracefully returns false on web.
 */
export async function requestPermission(): Promise<boolean> {
  if (!isNative()) return true; // web: no permission needed
  try {
    const { display } = await LocalNotifications.requestPermissions();
    return display === "granted";
  } catch {
    return false;
  }
}

/**
 * Schedule a local notification after `seconds` with the given title.
 * Gracefully no-ops on web (where Capacitor plugins aren't available).
 */
export async function scheduleTimerNotification(
  seconds: number,
  title: string,
  id: number = TIMER_NOTIFICATION_ID,
): Promise<void> {
  if (!isNative()) return;
  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title,
          body: "Le minuteur de cuisine a sonné !",
          schedule: { at: new Date(Date.now() + seconds * 1000) },
          sound: "default",
          smallIcon: "ic_stat_icon_config_sample",
          ongoing: false,
          autoCancel: true,
        },
      ],
    });
  } catch {
    // Plugin not available — no-op.
  }
}

/**
 * Cancel a pending timer notification (the default one when no id is given).
 * Gracefully no-ops on web.
 */
export async function cancelAll(
  id: number = TIMER_NOTIFICATION_ID,
): Promise<void> {
  if (!isNative()) return;
  try {
    await LocalNotifications.cancel({
      notifications: [{ id }],
    });
  } catch {
    // Plugin not available — no-op.
  }
}

/**
 * Default notification ID for cooking timer notifications (scheduling a new
 * one with the same id replaces the old one).
 */
const TIMER_NOTIFICATION_ID = 42;

/**
 * Stable per-step notification id, so two steps' timers running at the same
 * time ("Pendant ce temps") each keep their own native alarm (audit M22).
 * Positive 31-bit int (Android requires a Java int), never the default id.
 */
export function timerNotificationId(stepKey: string | null): number {
  if (stepKey === null) return TIMER_NOTIFICATION_ID;
  let hash = 0;
  for (let i = 0; i < stepKey.length; i += 1) {
    hash = (Math.imul(hash, 31) + stepKey.charCodeAt(i)) | 0;
  }
  const id = hash & 0x7fffffff || 1;
  return id === TIMER_NOTIFICATION_ID ? id + 1 : id;
}
